import { ref, computed } from 'vue';
import { defineStore, storeToRefs } from 'pinia';
import { format } from 'date-fns';
import { useRoute } from 'vue-router';
import useHttp from '@/utils/httpClient';
import { useMobileDevices } from '@composables/useScreenSize';

import { useBookingQueryStore } from '@stores/bookingQuery.store';
import { useAppConfigStore } from '@stores/appConfig.store';
import { usePaymentSummaryStore } from '@/stores/paymentSummary.store';
import { useOffersStore } from '@stores/offers.store';
import { useAuthStore } from '@stores/auth.store';
import { useUserProfile } from '@stores/userProfile.store';
const { isMobile } = useMobileDevices();

const appConfigStore = useAppConfigStore();
const { applicationSetup } = storeToRefs(appConfigStore);

const bookingQueryStore = useBookingQueryStore();
const { activatedPromotionalCodes, query } = storeToRefs(bookingQueryStore);

const userProfileStore = useUserProfile();
const { userProfile } = storeToRefs(userProfileStore);

const http = useHttp();

const loading = ref(false);

const authStore = useAuthStore();
const { apikey, channelKey } = storeToRefs(authStore);

export const useReservationStore = defineStore('reservationStore', () => {
  const booked = ref(false);
  const reservations = ref({});
  const guestDetails = ref({
    first_name: '',
    last_name: '',
    phone: '',
    email: '',
    address: '',
    city: '',
    postal_code: '',
    country: '',
    state: '',
  });

  const route = useRoute();

  const addReservation = (params) => {
    const offersStore = useOffersStore();
    const {
      isMultiRoom,
      multiRoomList,
      currentRoom,
      missingMultipleSelection,
      availableRoomsToGroup,
      multiRoomAvailability,
    } = storeToRefs(offersStore);
    const { getRoomInfo, fillCurrentRoom, restoreAvailability } = offersStore;

    let roomRateCode = params.room_rate_code;
    const occupancy = query.value.occupancy[currentRoom.value];
    let reservationAddons = [];
    if (params.addons) {
      reservationAddons = params.addons.value.map((addon) => ({
        room_rate_code: roomRateCode,
        addon_id: addon.id,
        quantity: addon.quantity,
      }));
    }

    let reservationData = {
      checkin: query.value.startDate,
      checkout: query.value.endDate,
      occupancy: {
        room_rate_code: roomRateCode,
        adults: occupancy.nrAdults,
        children: occupancy.nrChildren,
        babies: occupancy.nrBabies,
        children_ages: occupancy.children_ages ?? [],
      },
      addons: reservationAddons,
      mealPlan: {
        room_rate_code: roomRateCode,
        meal_plan_id: params.mealPlan,
      },
      rate: params.rate,
      room: params.room,
      roomRate: params.roomRate,
    };

    if (params.rezlock) {
      reservationData.rezlock = params.rezlock;
      reservationData.rezlockTime = params.rezlockTime;
      reservationData.rezlockPrice = params.rezlockPrice;
    }

    if (isMultiRoom.value) {
      const alreadySelected = reservations.value[currentRoom.value]
        ? true
        : false;
      if (alreadySelected) {
        restoreAvailability(
          multiRoomList.value[currentRoom.value].room_rate.split('.')[1],
        );
      }
    }

    reservations.value[currentRoom.value] = reservationData;

    if (isMultiRoom.value) {
      fillCurrentRoom(currentRoom.value, params.room_rate_code);
      const allChildren = Object.values(multiRoomList.value).filter(
        (room) =>
          room.parent !== undefined &&
          room.parent == parseInt(currentRoom.value),
      );
      if (allChildren.length > 0) {
        let allowedToReplicateAll = true;
        allChildren.forEach((child) => {
          const room = getRoomInfo(params.room_rate_code.split('.')[1]);
          const occupancy = child.occupancy;
          if (room.max_adults < occupancy.nrAdults) {
            allowedToReplicateAll = false;
          }
          if (room.max_children < occupancy.nrChildren + occupancy.nrBabies) {
            allowedToReplicateAll = false;
          }
          if (
            room.max_persons <
            occupancy.nrAdults + occupancy.nrChildren + occupancy.nrBabies
          ) {
            allowedToReplicateAll = false;
          }
          if (
            multiRoomAvailability.value[room.room_type_code] === undefined ||
            multiRoomAvailability.value[room.room_type_code] <
              allChildren.length
          ) {
            allowedToReplicateAll = false;
          }
        });
        if (allowedToReplicateAll) {
          allChildren.forEach((child) => {
            restoreAvailability(
              multiRoomList.value[child.identifier].room_rate.split('.')[1],
            );
            fillCurrentRoom(child.identifier, params.room_rate_code);
            cloneReservation(currentRoom.value, child.identifier);
          });
        } else {
          allChildren.forEach((child) => {
            restoreAvailability(
              multiRoomList.value[child.identifier].room_rate.split('.')[1],
            );
            delete multiRoomList.value[child.identifier].parent;
            multiRoomList.value[child.identifier].selected = false;
            multiRoomList.value[child.identifier].room_rate = '';
          });
        }
      }

      if (missingMultipleSelection.value) {
        if (Object.keys(availableRoomsToGroup.value).length > 0) {
          return { redirect: null, showDrawer: true };
        } else {
          if (
            multiRoomList.value[currentRoom.value].selected &&
            currentRoom.value == Object.keys(multiRoomList.value).length - 1
          ) {
            saveSession();
            return { redirect: 'guest details', showDrawer: false };
          }
          return { redirect: 'search', showDrawer: false };
        }
      }
    }
    saveSession();
    return { redirect: 'guest details', showDrawer: false };
  };

  const cloneReservation = (indexToClone, newIndex) => {
    const occupancy = query.value.occupancy[newIndex];
    let reservationToClone = JSON.parse(
      JSON.stringify(reservations.value[indexToClone]),
    );
    reservationToClone.checkin = reservations.value[indexToClone].checkin;
    reservationToClone.checkout = reservations.value[indexToClone].checkout;
    reservationToClone.occupancy.adults = occupancy.nrAdults;
    reservationToClone.occupancy.children = occupancy.nrChildren;
    reservationToClone.occupancy.children_ages = occupancy.children_ages ?? [];
    reservationToClone.occupancy.babies = occupancy.nrBabies;
    reservations.value[newIndex] = reservationToClone;
  };

  const setPaymentData = (params) => {
    for (let i in reservations.value) {
      reservations.value[i].credit_card = params;
    }
  };

  const setReservationDetails = (params) => {
    for (let i in reservations.value) {
      reservations.value[i].motiveOfStay = params.motiveOfStay;
      reservations.value[i].notes = params.notes;
      reservations.value[i].subscribeNewsletter = params.subscribeNewsletter;
    }
    setGuestDetails(params);
  };

  const setGuestDetails = (params) => {
    if (params.first_name) {
      guestDetails.value.first_name = params.first_name;
    }
    if (params.last_name) {
      guestDetails.value.last_name = params.last_name;
    }
    if (params.email) {
      guestDetails.value.email = params.email;
    }
    if (params.phone) {
      guestDetails.value.phone = params.phone;
    }
    if (params.address) {
      guestDetails.value.address = params.address;
    }
    if (params.city) {
      guestDetails.value.city = params.city;
    }
    if (params.state) {
      guestDetails.value.state = params.state;
    }
    if (params.postal_code) {
      guestDetails.value.postal_code = params.postal_code;
    }
    if (params.country) {
      guestDetails.value.country = params.country;
    }
    if (params.countryCode) {
      guestDetails.value.countryCode = params.countryCode;
    }
    if (params.motiveOfStay) {
      guestDetails.value.motiveOfStay = params.motiveOfStay;
    }
    if (params.notes) {
      guestDetails.value.notes = params.notes;
    }
    if (params.subscribeNewsletter) {
      guestDetails.value.subscribeNewsletter = params.subscribeNewsletter;
    }
    if (params.aditionalFields) {
      guestDetails.value.aditionalFields = params.aditionalFields;
    }
    saveUserSession();
  };

  const book = async () => {
    let reservationsData = buildReservationsData();
    reservationsData.marketing = {};
    if (route.query.utm_source) {
      reservationsData.marketing.utm_source = route.query.utm_source;
    }
    if (route.query.utm_medium) {
      reservationsData.marketing.utm_medium = route.query.utm_medium;
    }
    if (route.query.utm_campaign) {
      reservationsData.marketing.utm_campaign = route.query.utm_campaign;
    }
    if (route.query.utm_term) {
      reservationsData.marketing.utm_term = route.query.utm_term;
    }
    if (route.query.utm_content) {
      reservationsData.marketing.utm_content = route.query.utm_content;
    }

    loading.value = true;
    try {
      let url =
        applicationSetup.value.hotel_id + '/book?apikey=' + apikey.value;
      if (channelKey.value) {
        url += '&channelKey=' + channelKey.value;
      }
      const results = await http.post(url, reservationsData).then((rsp) => {
        booked.value = rsp.data;
        saveBookSession();
        return rsp.data;
      });
      loading.value = false;

      return results;
    } catch (error) {
      loading.value = false;
    }
  };

  const buildReservationsData = () => {
    const { campaignCode, promotionCodes } = computed(() => {
      return activatedPromotionalCodes.value.reduce(
        (acc, code) => {
          if (code.isCampaignCode) {
            acc.campaignCode = code.value;
          } else {
            acc.promotionCodes.push(code.value);
          }
          return acc;
        },
        { campaignCode: '', promotionCodes: [] },
      );
    }).value;

    const paymentSummaryStore = usePaymentSummaryStore();
    let epg = {};
    const payments = paymentSummaryStore.paymentSummaryDetails.payment_options;
    payments.forEach((payment) => {
      if (payment.type === 'external') {
        epg = {
          name: payment.className,
          validation: payment.validation,
        };
      }
    });

    let reservationsData = [];
    for (let i = 0; i < Object.keys(reservations.value).length; i++) {
      let reservation = reservations.value[i];

      let reservationSummary =
        paymentSummaryStore.paymentSummaryDetails.summary.occupancies[i];

      let hotelAddons = paymentSummaryStore.paymentSummaryDetails.summary
        .hotel_addons
        ? Object.values(
            paymentSummaryStore.paymentSummaryDetails.summary.hotel_addons,
          )
        : [];
      let reservationAddons = reservationSummary.room_rate.room_addons ?? [];
      let mergedAddons = reservationAddons;
      if (i == 0) {
        mergedAddons = [...hotelAddons, ...reservationAddons];
      }
      // THIS SHOULD BE REPLACED BY THE API CALL TO SUMMARY @todo
      let mappedAddons = mergedAddons.map((addon) => {
        const addonObj = addon;
        addonObj.net_price = addon.unit_price;
        addonObj.image = '';
        return addonObj;
      });

      if (reservation) {
        const apiFormatted = {
          crs_reservation_code: '',
          hotel_reservation_code: '',
          action: 'new',
          checkin: format(query.value.startDate, 'yyyy-MM-dd'),
          checkout: format(query.value.endDate, 'yyyy-MM-dd'),
          number_of_rooms: 1,
          number_of_adults: reservation.occupancy.adults,
          number_of_children: reservation.occupancy.children,
          children_ages: reservation.occupancy.children_ages ?? [],
          number_of_babies: reservation.occupancy.babies,
          book_date: new Date().toISOString(),
          modified_date: new Date().toISOString(),
          cancelled_date: '',
          notes: reservation.notes,
          motive_of_stay: reservation.motiveOfStay,
          status: 'confirmed',
          booking_referer: query.value.referrer,
          register_newsletter: reservation.subscribeNewsletter,
          hotel: {
            hotel_id: applicationSetup.value.hotel_id,
            hotel_name: applicationSetup.value.hotel_name,
            hotel_reservations_email: applicationSetup.value
              .contacts_information.email_reservations
              ? applicationSetup.value.contacts_information.email_reservations
              : applicationSetup.value.contacts_information.email,
          },
          guest: {
            first_name: guestDetails.value.first_name,
            last_name: guestDetails.value.last_name,
            phone: guestDetails.value.phone,
            email: guestDetails.value.email,
            address: guestDetails.value.address,
            city: guestDetails.value.city,
            postal_code: guestDetails.value.postal_code,
            country: guestDetails.value.country,
            country_code: guestDetails.value.countryCode,
            state: guestDetails.value.state,
            language: applicationSetup.value.language,
          },
          room_type: reservationSummary.room,
          rate_plan: reservationSummary.rate_plan,
          room_rate: reservationSummary.room_rate,
          meal_plan: getMealPlan(reservationSummary),
          cancellation_policy: reservationSummary.room_rate.cancellation_policy,
          addons: mappedAddons,
          credit_card: {
            name: '',
            month: '',
            year: '',
            transactionId: '',
          },
          epg: epg,
          is_mobile: isMobile.value,
          login: userProfile.value.email || userProfile.value.playerId || '',
        };
        apiFormatted.additional_fields = [];
        if (guestDetails.value.aditionalFields) {
          Object.keys(guestDetails.value.aditionalFields).forEach((key) => {
            const additionalField =
              paymentSummaryStore.paymentSummaryDetails.additional_fields.find(
                (item) => item.request_code === key,
              );

            if (additionalField) {
              const field = guestDetails.value.aditionalFields[key];
              apiFormatted.additional_fields.push({
                [additionalField.id]: field,
              });
            }
          });
        }

        if (reservation.rezlock) {
          apiFormatted.rezLockTimeSelected = reservation.rezlockTime;
          apiFormatted.rezLockTotal = reservation.rezlockPrice;
        }

        if (reservation.credit_card) {
          apiFormatted.credit_card = reservation.credit_card;
        }

        if (campaignCode) {
          apiFormatted.sap_code = campaignCode;
        }

        if (promotionCodes.length > 0) {
          apiFormatted.promotion_code = promotionCodes;
        }

        if (reservation.voucher) {
          apiFormatted.voucher = reservation.voucher;
        }

        reservationsData.push(apiFormatted);
      }
    }

    return {
      reservations: reservationsData,
      summary: paymentSummaryStore.summaryParams,
    };
  };

  const getMealPlan = (reservationSummary) => {
    if (reservationSummary.room_rate.meal_plan.id) {
      return reservationSummary.room_rate.meal_plan;
    }
    return reservationSummary.room_rate.meal_plan_prices.included;
  };

  const loadSession = () => {
    const session = sessionStorage.getItem(
      'reservationOptions' + applicationSetup.value.hotel_id,
    );
    if (session) {
      const sessionData = JSON.parse(session);
      reservations.value = sessionData;

      if (sessionData[0]) {
        let firstRoomValues = sessionData[0];
        if (firstRoomValues.checkin) {
          query.value.startDate = firstRoomValues.checkin;
        }
        if (firstRoomValues.checkout) {
          query.value.endDate = firstRoomValues.checkout;
        }
      }

      let queryOccupancies = [];
      Object.keys(sessionData).forEach((room) => {
        room = sessionData[room];
        queryOccupancies.push({
          nrAdults: room.occupancy.adults,
          nrChildren: room.occupancy.children,
          nrBabies: room.occupancy.babies,
          children_ages: room.occupancy.children_ages ?? [],
        });
      });
      query.value.occupancy = queryOccupancies;
    }
    const offersStore = useOffersStore();
    const { multiRoomList, multiRoomAvailability } = storeToRefs(offersStore);

    const multiRoomListSession = sessionStorage.getItem(
      'multiRoomList' + applicationSetup.value.hotel_id,
    );
    if (multiRoomListSession) {
      multiRoomList.value = JSON.parse(multiRoomListSession);
    }
    const multiRoomAvailabilitySession = sessionStorage.getItem(
      'multiRoomAvailability' + applicationSetup.value.hotel_id,
    );
    if (multiRoomAvailabilitySession) {
      multiRoomAvailability.value = JSON.parse(multiRoomAvailabilitySession);
    }
  };

  const loadUserSession = () => {
    const session = sessionStorage.getItem(
      'guestDetails' + applicationSetup.value.hotel_id,
    );
    if (session) {
      const sessionData = JSON.parse(session);
      setReservationDetails(sessionData);
    }
  };

  const loadBookSession = () => {
    const session = sessionStorage.getItem(
      'bookDetails' + applicationSetup.value.hotel_id,
    );
    if (session) {
      const sessionData = JSON.parse(session);
      booked.value = sessionData;
    }
  };

  const saveSession = () => {
    sessionStorage.setItem(
      'reservationOptions' + applicationSetup.value.hotel_id,
      JSON.stringify(reservations.value),
    );
    const offersStore = useOffersStore();
    const { multiRoomList, multiRoomAvailability } = storeToRefs(offersStore);
    sessionStorage.setItem(
      'multiRoomList' + applicationSetup.value.hotel_id,
      JSON.stringify(multiRoomList.value),
    );
    sessionStorage.setItem(
      'multiRoomAvailability' + applicationSetup.value.hotel_id,
      JSON.stringify(multiRoomAvailability.value),
    );
  };

  const saveUserSession = () => {
    sessionStorage.setItem(
      'guestDetails' + applicationSetup.value.hotel_id,
      JSON.stringify(guestDetails.value),
    );
  };

  const saveBookSession = () => {
    sessionStorage.setItem(
      'bookDetails' + applicationSetup.value.hotel_id,
      JSON.stringify(booked.value),
    );
  };

  const sendEmail = async (email, reservationCode) => {
    let url =
      applicationSetup.value.hotel_id +
      `/${reservationCode}/resend_email?apikey=` +
      apikey.value;

    const response = await http.post(url, { email });

    return response;
  };

  const resetReservation = () => {
    const offersStore = useOffersStore();
    const { currentRoom, multiRoomAvailability, multiRoomList } =
      storeToRefs(offersStore);
    const { buildMultiRoomList } = offersStore;
    reservations.value = [];
    currentRoom.value = 0;
    multiRoomAvailability.value = {};
    multiRoomList.value = {};
    buildMultiRoomList();
  };

  const clearBookSession = () => {
    resetReservation();
    booked.value = false;
    sessionStorage.removeItem('bookDetails' + applicationSetup.value.hotel_id);
    sessionStorage.removeItem(
      'reservationOptions' + applicationSetup.value.hotel_id,
    );
  };

  const setVoucherCode = (params) => {
    for (let i in reservations.value) {
      reservations.value[i].voucher = params;
    }
  };

  return {
    reservations,
    loading,
    guestDetails,
    booked,
    addReservation,
    book,
    setReservationDetails,
    setPaymentData,
    loadSession,
    loadUserSession,
    loadBookSession,
    sendEmail,
    resetReservation,
    cloneReservation,
    clearBookSession,
    setVoucherCode,
  };
});
